import { Grid, Stack, TextField, Typography } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { getFormattedTemplateStringFromFieldKey } from "utils/contacts/bulkMessage/helper";
import type { BulkMessageRecipient } from "utils/contacts/bulkMessage/new";
import { type Dispatch, type ChangeEvent, type SetStateAction } from "react";
import {
  type BulkMessageTemplateType,
  type PlaceholderTemplateForm,
  type AllBulkMessageTemplateKeys,
  getMissingFieldContactsCount
} from "utils/contacts/bulkMessage/new";

type BulkMessageDialogTemplatePlaceholderProps = {
  /**
   * The recipients, as the dialog resolved them. Passed in rather than read from
   * `useLegacyContactsBook().allContacts`: that array is the whole book and is EMPTY on every host that
   * has not downloaded it, where the counts below were computed against nobody.
   */
  audience: BulkMessageRecipient[];
  selected: string[];
  messageInput: string;
  bulkMessageType: BulkMessageTemplateType;
  placeholderTemplatesForm: PlaceholderTemplateForm;
  setPlaceholderTemplatesForm: Dispatch<SetStateAction<PlaceholderTemplateForm>>;
};

export function BulkMessageDialogTemplatePlaceholder({
  selected,
  audience,
  messageInput,
  bulkMessageType,
  placeholderTemplatesForm,
  setPlaceholderTemplatesForm
}: BulkMessageDialogTemplatePlaceholderProps): JSX.Element {
  const { t: translate } = useTranslation();

  const handlePlaceholderTemplateChange =
    (template: string) =>
    (event: ChangeEvent<HTMLInputElement>): void => {
      setPlaceholderTemplatesForm({
        ...placeholderTemplatesForm,
        [template]: event.target.value
      });
    };

  const missingFieldCounts = getMissingFieldContactsCount(selected, audience);

  const visibleFieldsTemplate = Object.entries(missingFieldCounts)
    .filter(([_, missingFieldCount]) => missingFieldCount < selected.length)
    .map(([key, _]) => key);

  const visibleFieldsPlaceholder = visibleFieldsTemplate.filter(
    (key) =>
      missingFieldCounts[key] &&
      messageInput.includes(
        getFormattedTemplateStringFromFieldKey(key as AllBulkMessageTemplateKeys)
      )
  );

  if (!visibleFieldsPlaceholder.length) return null;

  return (
    <Stack gap={2}>
      <Typography variant="body1">
        {translate("contactsPage.bulkMessage.addPlaceholderIfEmpty")}
      </Typography>
      <Grid container spacing={2}>
        {visibleFieldsPlaceholder.map((template) => {
          const missingCount = missingFieldCounts[template];

          return (
            <Grid item xs={6} key={template}>
              <TextField
                fullWidth
                label={translate(`contactsPage.bulkMessage.templates.contact.fields.${template}`)}
                value={placeholderTemplatesForm[template]}
                helperText={
                  !!missingCount &&
                  bulkMessageType === "contact" &&
                  translate("contactsPage.bulkMessage.missingFieldInfo", {
                    contactsCount: missingCount
                  })
                }
                onChange={handlePlaceholderTemplateChange(template)}
              />
            </Grid>
          );
        })}
      </Grid>
    </Stack>
  );
}
